<script setup lang="ts">
import { ref, inject, onBeforeMount, toRefs, computed, Ref } from "vue";

import { useApiClient } from "@/api/client";
import { required } from "@/ui/validation/validationRules";
import { t } from "@/plugins/i18n";
import { useBaseStore } from "@/stores/base";
import PublicUserDO = App.Data.PublicUserDO;
import RoleDO = App.Data.RoleDO;
import { toastApiResult } from "@/toaster";
import AbilityName = App.Enums.AbilityName;
import SharedResourcePermissionDTO = App.Data.SharedResourcePermissionDTO;

type SharedRolePermission = {
	role: RoleDO;
	abilities: AbilityName[];
};

type ListUsersWithPermissions = (resource: object) => Promise<any>;
const listFiles = inject<ListUsersWithPermissions>("listUsersWithPermissions");

type RemovePermission = (resource: object, user: PublicUserDO) => Promise<any>;
const removePermission = inject<RemovePermission>("removePermissionAction");

const emit = defineEmits(["reload-files-in-selected-folder"]);

type GrantPermission = (
	resource: object,
	user: PublicUserDO,
	ability: AbilityName
) => Promise<any>;
const grantPermission = inject<GrantPermission>("grantPermissionAction");

type ListRolesWithPermissions = (resource: object) => Promise<any>;
const listRolesForResource = inject<ListRolesWithPermissions>(
	"listRolesWithPermissions"
);

type RemoveRolePermission = (resource: object, role: RoleDO) => Promise<any>;
const removeRolePermission = inject<RemoveRolePermission>(
	"removeRolePermissionAction"
);

type GrantRolePermission = (
	resource: object,
	role: RoleDO,
	ability: AbilityName
) => Promise<any>;
const grantRolePermission = inject<GrantRolePermission>(
	"grantRolePermissionAction"
);

const canManageRoles = computed(
	(): boolean =>
		!!useBaseStore().currentUser?.derivedPermissions?.includes(
			"can_manage_roles"
		)
);

const isRoleSharingAvailable = computed(
	(): boolean => canManageRoles.value && !!listRolesForResource
);

const props = defineProps<{
	resourceToManage: object;
}>();

const { resourceToManage } = toRefs(props);

const isDialogVisible: Ref<boolean> = defineModel<boolean>();
const isLoading: Ref<boolean> = ref(true);
const usersWithPermissions: Ref<SharedResourcePermissionDTO[]> = ref([]);
const availableUsers: Ref<PublicUserDO[]> = ref([]);
const userToPermit: Ref<PublicUserDO> = ref();
const abilityForUserToPermit: Ref<AbilityName> = ref("view");
const isFormValid: Ref<boolean> = ref(false);

const activeTab: Ref<string> = ref("users");
const isLoadingRoles: Ref<boolean> = ref(true);
const rolesWithPermissions: Ref<SharedRolePermission[]> = ref([]);
const availableRoles: Ref<RoleDO[]> = ref([]);
const roleToPermit: Ref<RoleDO> = ref();
const abilityForRoleToPermit: Ref<AbilityName> = ref("view");
const isRoleFormValid: Ref<boolean> = ref(false);

const filteredUsers = computed((): PublicUserDO[] => {
	const userIds = [useBaseStore().currentUser.id];
	usersWithPermissions.value.forEach((userWithPermissions) =>
		userIds.push(userWithPermissions.user.id)
	);
	return availableUsers.value.filter((user) => !userIds.includes(user.id));
});

const filteredRoles = computed((): RoleDO[] => {
	const roleIds = rolesWithPermissions.value.map(
		(roleWithPermissions) => roleWithPermissions.role.id
	);
	return availableRoles.value.filter((role) => !roleIds.includes(role.id));
});

const headers = computed(() => [
	{
		title: t("fileBrowser.share.user"),
		align: "start" as const,
		key: "user",
		sortable: false,
	},
	{
		title: t("fileBrowser.share.abilities"),
		align: "start" as const,
		key: "abilities",
		sortable: false,
	},
	{
		title: t("fileBrowser.share.revoke"),
		align: "end" as const,
		key: "revoke",
		sortable: false,
	},
]);

const roleHeaders = [
	{
		title: "Role",
		align: "start",
		key: "role",
		sortable: false,
	},
	{
		title: "Abilities",
		align: "start",
		key: "abilities",
		sortable: false,
	},
	{
		title: "Revoke",
		align: "end",
		key: "revoke",
		sortable: false,
	},
];

const availableAbilities = computed(() => [
	{ name: t("fileBrowser.share.read"), value: "view" },
	{ name: t("fileBrowser.share.updateAbility"), value: "update" },
	{ name: t("fileBrowser.share.everything"), value: "destroy" },
]);

const getHighestAbility = (abilities) => {
	for (const ability of [...availableAbilities.value].reverse()) {
		if (abilities.includes(ability.value)) {
			return ability.name;
		}
	}
};

const fetchUsersWithPermissions = async () => {
	isLoading.value = true;
	const response = await listFiles(resourceToManage.value);
	if (response.isOk()) {
		usersWithPermissions.value = response.value;
	}
	isLoading.value = false;
};

const fetchAvailableUsers = async () => {
	const response = await useApiClient("users").getEnvironmentUsers();
	if (response.isOk()) {
		availableUsers.value = response.value;
	}
};

const revokePermissionForUser = async (user: PublicUserDO) => {
	const response = await toastApiResult(
		removePermission(resourceToManage.value, user)
	);
	if (response.isOk()) {
		isDialogVisible.value = false;
		emit("reload-files-in-selected-folder");
	}
};

const grantPermissionForUser = async () => {
	const response = await toastApiResult(
		grantPermission(
			resourceToManage.value,
			userToPermit.value,
			abilityForUserToPermit.value
		)
	);
	if (response.isOk()) {
		isDialogVisible.value = false;
		emit("reload-files-in-selected-folder");
	}
};

const fetchRolesWithPermissions = async () => {
	if (!isRoleSharingAvailable.value) {
		isLoadingRoles.value = false;
		return;
	}
	isLoadingRoles.value = true;
	const response = await listRolesForResource(resourceToManage.value);
	if (response.isOk()) {
		rolesWithPermissions.value = response.value;
	}
	isLoadingRoles.value = false;
};

const fetchAvailableRoles = async () => {
	if (!isRoleSharingAvailable.value) {
		return;
	}
	const response = await useApiClient("roles").getRoles({ per_page: 100 });
	if (response.isOk()) {
		availableRoles.value = response.value.data;
	}
};

const revokePermissionForRole = async (role: RoleDO) => {
	const response = await toastApiResult(
		removeRolePermission(resourceToManage.value, role)
	);
	if (response.isOk()) {
		isDialogVisible.value = false;
		emit("reload-files-in-selected-folder");
	}
};

const grantPermissionForRole = async () => {
	const response = await toastApiResult(
		grantRolePermission(
			resourceToManage.value,
			roleToPermit.value,
			abilityForRoleToPermit.value
		)
	);
	if (response.isOk()) {
		isDialogVisible.value = false;
		emit("reload-files-in-selected-folder");
	}
};

onBeforeMount(() => {
	fetchAvailableUsers();
	fetchUsersWithPermissions();
	fetchAvailableRoles();
	fetchRolesWithPermissions();
});
</script>

<template>
	<v-dialog
		v-model="isDialogVisible"
		scrim="#000000"
		overlay-color="black"
		max-width="50vw"
	>
		<v-card>
			<v-toolbar>
				<template v-slot:prepend>
					<v-icon start class="ml-3 mr-5"
						>fa: fa-duotone fa-rectangle-history-circle-user</v-icon
					>
					<v-toolbar-title class="pt-1">
						{{ t("fileBrowser.share.title") }}
					</v-toolbar-title>
				</template>
				<v-spacer></v-spacer>

				<v-btn icon @click="isDialogVisible = false">
					<v-icon>mdi-close</v-icon>
				</v-btn>

				<template v-if="canManageRoles" v-slot:extension>
					<v-tabs v-model="activeTab">
						<v-tab value="users">Users</v-tab>
						<v-tab value="roles">Roles</v-tab>
					</v-tabs>
				</template>
			</v-toolbar>
			<v-divider />
			<v-window v-model="activeTab">
				<v-window-item value="users">
					<v-data-table
						density="compact"
						hide-default-header
						hide-default-footer
						:items="usersWithPermissions"
						:headers="headers"
						:loading="isLoading"
					>
						<template #no-data>
							<v-alert
								v-if="!isLoading"
								class="pa-5 my-5"
								color="info"
								border="start"
								variant="tonal"
							>
								This resource is not yet shared with anyone.
							</v-alert>
						</template>

						<template v-slot:item.user="{ item }">
							<div class="d-flex align-items-center mt-2 mb-2">
								<v-chip
									color="secondary"
									density="compact"
									size="48"
									class="py-3 px-3"
								>
									<v-icon>fa: fa-duotone fa-user-vneck</v-icon>
								</v-chip>
								<div
									class="mx-3 pt-3"
									style="
										text-wrap: nowrap;
										max-width: 250px;
										overflow: hidden;
										text-overflow: ellipsis;
									"
								>
									{{ item.user.name }}
								</div>
							</div>
						</template>
						<template v-slot:item.abilities="{ item }">
							<v-chip
								v-tooltip="`${item.abilities}`"
								class="mr-5 my-2"
								label
								density="compact"
								>{{ getHighestAbility(item.abilities) }}</v-chip
							>
						</template>
						<template v-slot:item.revoke="{ item }">
							<v-btn
								@click="revokePermissionForUser(item.user)"
								color="error"
								density="compact"
								variant="tonal"
								prepend-icon="fa: fa-light fa-user-large-slash"
								>Revoke</v-btn
							>
						</template>
					</v-data-table>
					<v-divider />
					<v-form v-model="isFormValid">
						<div class="pa-5">
							<div class="pb-5">
								If you want to share this resource with another user you can
								configure the user and permission below:
							</div>
							<v-row dense>
								<v-col cols="5">
									<v-autocomplete
										hide-details
										label="User"
										placeholder="Select a user"
										:items="filteredUsers"
										item-title="name"
										item-value="id"
										v-model="userToPermit"
										:rules="[required(t)]"
										return-object
									/>
								</v-col>
								<v-col cols="5">
									<v-select
										hide-details
										label="Permission"
										:items="availableAbilities"
										item-title="name"
										item-value="value"
										v-model="abilityForUserToPermit"
										:rules="[required(t)]"
									/>
								</v-col>
								<v-col cols="2">
									<v-btn
										block
										height="48"
										:disabled="isFormValid === false"
										color="secondary"
										variant="flat"
										@click="grantPermissionForUser"
									>
										Grant
									</v-btn>
								</v-col>
							</v-row>
						</div>
					</v-form>
				</v-window-item>

				<v-window-item v-if="canManageRoles" value="roles">
					<v-alert
						v-if="!isRoleSharingAvailable"
						class="pa-5 ma-5"
						color="info"
						border="start"
						variant="tonal"
					>
						Sharing with roles is not yet available for this resource.
					</v-alert>
					<template v-else>
						<v-data-table
							density="compact"
							hide-default-header
							hide-default-footer
							:items="rolesWithPermissions"
							:headers="roleHeaders"
							:loading="isLoadingRoles"
						>
							<template #no-data>
								<v-alert
									v-if="!isLoadingRoles"
									class="pa-5 my-5"
									color="info"
									border="start"
									variant="tonal"
								>
									This resource is not yet shared with any role.
								</v-alert>
							</template>

							<template v-slot:item.role="{ item }">
								<div class="d-flex align-items-center mt-2 mb-2">
									<v-chip
										color="secondary"
										density="compact"
										size="48"
										class="py-3 px-3"
									>
										<v-icon>fa: fa-duotone fa-user-group</v-icon>
									</v-chip>
									<div
										class="mx-3 pt-3"
										style="
											text-wrap: nowrap;
											max-width: 250px;
											overflow: hidden;
											text-overflow: ellipsis;
										"
									>
										{{ item.role.name }}
									</div>
								</div>
							</template>
							<template v-slot:item.abilities="{ item }">
								<v-chip
									v-tooltip="`${item.abilities}`"
									class="mr-5 my-2"
									label
									density="compact"
									>{{ getHighestAbility(item.abilities) }}</v-chip
								>
							</template>
							<template v-slot:item.revoke="{ item }">
								<v-btn
									@click="revokePermissionForRole(item.role)"
									color="error"
									density="compact"
									variant="tonal"
									prepend-icon="fa: fa-light fa-user-large-slash"
									>Revoke</v-btn
								>
							</template>
						</v-data-table>
						<v-divider />
						<v-form v-model="isRoleFormValid">
							<div class="pa-5">
								<div class="pb-5">
									If you want to share this resource with a role you can
									configure the role and permission below:
								</div>
								<v-row dense>
									<v-col cols="5">
										<v-autocomplete
											hide-details
											label="Role"
											placeholder="Select a role"
											:items="filteredRoles"
											item-title="name"
											item-value="id"
											v-model="roleToPermit"
											:rules="[required(t)]"
											return-object
										/>
									</v-col>
									<v-col cols="5">
										<v-select
											hide-details
											label="Permission"
											:items="availableAbilities"
											item-title="name"
											item-value="value"
											v-model="abilityForRoleToPermit"
											:rules="[required(t)]"
										/>
									</v-col>
									<v-col cols="2">
										<v-btn
											block
											height="48"
											:disabled="isRoleFormValid === false"
											color="secondary"
											variant="flat"
											@click="grantPermissionForRole"
										>
											Grant
										</v-btn>
									</v-col>
								</v-row>
							</div>
						</v-form>
					</template>
				</v-window-item>
			</v-window>
		</v-card>
	</v-dialog>
</template>

<style scoped lang="scss"></style>
